import axios from 'axios'

/** Error codes emitted by the auth micro-service. */
export const ApiErrorCode = {
  Unauthorized: 'UNAUTHORIZED',
  /** Main API domain codes, which it returns in `statusCode`. */
  InvalidSession: '900',
  InvalidInstallation: '650',
  ValidationError: 'VALIDATION_ERROR',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  AccountInactive: 'ACCOUNT_INACTIVE',
  InvalidRefreshToken: 'INVALID_REFRESH_TOKEN',
  InvalidToken: 'INVALID_TOKEN',
  Network: 'NETWORK_ERROR',
  /** The request's host is not set for this environment. Raised here, never by a server. */
  NotConfigured: 'NOT_CONFIGURED',
  Unknown: 'UNKNOWN_ERROR',
} as const

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode]

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Field-level messages, flattened to one string per offending field. */
  readonly details: string[]
  /**
   * True when `message` is something a person was meant to read — the service's
   * own wording, or copy this app wrote. False when it is only axios describing
   * the transport ("Request failed with status code 500"), which says nothing a
   * merchant can act on and must never reach the screen.
   */
  readonly hasUserMessage: boolean
  /**
   * The server's id for this failure, when it sends one (the inventory service
   * does). Not for the screen — it is what a bug report needs to find the request.
   */
  readonly traceId?: string

  constructor(
    message: string,
    options: {
      status: number
      code: string
      details?: string[]
      hasUserMessage?: boolean
      traceId?: string
    },
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.code = options.code
    this.details = options.details ?? []
    this.hasUserMessage = options.hasUserMessage ?? true
    this.traceId = options.traceId
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /**
   * True when the session is the problem and a token refresh is worth trying.
   * The auth service says 401; the main API answers with HTTP 400 carrying
   * either domain code 900 or a "No user signed in." message.
   */
  get isSessionExpired(): boolean {
    return (
      this.status === 401 ||
      this.code === ApiErrorCode.InvalidSession ||
      /no user signed in/i.test(this.message)
    )
  }
}

/** Below this, `statusCode` is an HTTP status; at or above it, a domain code. */
const DOMAIN_CODE_FLOOR = 600

/** `{ error: { code, message, fields } }` — what the deployed service returns. */
interface ErrorEnvelope {
  code?: string
  message?: string
  fields?: Record<string, string>
}

interface ErrorBody {
  error?: string | ErrorEnvelope
  code?: string
  message?: string | string[]
  statusCode?: number
  /** The inventory service's field issues, `{ field, message }` each. */
  details?: unknown
  traceId?: string
}

function envelopeOf(data: ErrorBody | undefined): ErrorEnvelope | null {
  const error = data?.error
  return typeof error === 'object' && error !== null ? error : null
}

/**
 * The messages out of the inventory service's `details`.
 *
 * Each `message` already leads with its field ("limit must not be greater than
 * 100"), so the field is not prefixed again. Entries without one are skipped:
 * the same array carries non-field data too — a stock shortage lists items and
 * quantities — and none of that is a sentence to show.
 *
 * A recipe modifier's components are refused with `{ reason }` entries rather
 * than `{ message }` — one sentence per broken rule, under the generic "Invalid
 * modifier components." — so those are read as well.
 */
function detailMessages(details: unknown): string[] {
  if (!Array.isArray(details)) return []

  return details.flatMap((issue: unknown) => {
    const entry = issue as { message?: unknown; reason?: unknown } | null
    const message = entry?.message ?? entry?.reason
    return typeof message === 'string' && message.trim() ? [message] : []
  })
}

/**
 * Normalises everything axios can throw into a single `ApiError`.
 *
 * Four response shapes are handled: the service's own
 * `{ error: { code, message, fields } }` envelope, a flat `{ code, message }`,
 * Nest's default `{ statusCode, error, message: string[] }` from the
 * `ValidationPipe`, and the inventory service's
 * `{ code, message, traceId, details: [{ field, message }] }`.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError<ErrorBody>(error)) {
    if (!error.response) {
      return new ApiError('Cannot reach the server. Check your connection and try again.', {
        status: 0,
        code: error.code === 'ECONNABORTED' ? 'TIMEOUT' : ApiErrorCode.Network,
      })
    }

    const { status, data } = error.response
    const envelope = envelopeOf(data)

    const details = envelope?.fields
      ? Object.entries(envelope.fields).map(([field, message]) => `${field} ${message}`)
      : Array.isArray(data?.message)
        ? data.message
        : detailMessages(data?.details)

    // A field-level complaint says more than the generic "Validation failed."
    const serverMessage =
      details[0] ??
      envelope?.message ??
      (typeof data?.message === 'string' ? data.message : undefined) ??
      (typeof data?.error === 'string' ? data.error : undefined)

    const domainCode =
      typeof data?.statusCode === 'number' && data.statusCode >= DOMAIN_CODE_FLOOR
        ? String(data.statusCode)
        : undefined

    // Without a body there is nothing but axios's own transport line, which is
    // kept for logs and marked unusable so callers fall back to their own copy.
    return new ApiError(serverMessage ?? error.message, {
      status,
      code: envelope?.code ?? data?.code ?? domainCode ?? ApiErrorCode.Unknown,
      details,
      hasUserMessage: Boolean(serverMessage?.trim()),
      traceId: typeof data?.traceId === 'string' ? data.traceId : undefined,
    })
  }

  if (error instanceof Error) {
    return new ApiError(error.message, {
      status: 0,
      code: ApiErrorCode.Unknown,
      hasUserMessage: false,
    })
  }

  return new ApiError('Something went wrong. Please try again.', {
    status: 0,
    code: ApiErrorCode.Unknown,
  })
}

/**
 * What to put in front of the merchant: the service's own words whenever it sent
 * any, and the caller's copy only when it did not. Every error surface in the app
 * goes through here so a failure reads the same wherever it lands.
 */
export function errorMessage(error: unknown, fallback: string): string {
  const apiError = toApiError(error)

  return apiError.hasUserMessage && apiError.message.trim() ? apiError.message : fallback
}
